import { useState } from 'react';
import { ChatCircle, Copy, DownloadSimple, InstagramLogo, PencilSimple, UploadSimple, X } from '@phosphor-icons/react';
import type { MappedApiError } from '@mybusinessmate/api-client';
import { plans } from '@mybusinessmate/ui-tokens';
import {
  CLIENT_REVISION_MIN,
  REVISIONS_USED_CODE,
  type ClientReview,
  type ClientVersion,
} from '@mybusinessmate/schema';
import { captionOf, detailRows, feedbackAreas, goesLive, madeBy, sentAgo } from '../../lib/client-review-display';
import { signedDownloadUrl } from '../../lib/asset-url';
import { cx } from '../../lib/cx';
import { TASK_TYPE_META, weekdayDay } from '../../lib/task-display';
import { useClientRespond } from '../../lib/tasks-api';
import { Icon } from '../Icon';
import { Button } from '../ui/button';
import { StatusTag } from '../ui/status-tag';
import { useToast } from '../ui/toast';
import { toasts } from '../../lib/toast-presets';

/**
 * What one more change round costs. TODO(billing): comes from the plan once billing exists; until then it is the
 * price on the design, and buying is a "Coming soon" note.
 */
const EXTRA_ROUND_PRICE = '₹299';

/** The cards beside the stage (Figma C05.2): white, 24 radius, a soft lift, 22 inside. */
const CARD =
  'flex flex-col gap-(--mbm-space-3-5) rounded-(--mbm-component-radius-widget) bg-surface-card p-(--mbm-space-5-5) shadow-(--mbm-shadow-table-card)';

/** "On image 2, make the bowl bigger (Image 2, Colours)": the words the client wrote, without the chips we added. */
const wordsOf = (feedback: string | null) => (feedback ?? '').replace(/\s\([^()]*\)$/, '');

function Head({ title, tag }: { title: string; tag?: { label: string; tone: 'amber' | 'green' } }) {
  return (
    <div className="flex items-center justify-between gap-(--mbm-space-3)">
      <h2 className="text-heading-h3 text-text-primary">{title}</h2>
      {tag ? <StatusTag label={tag.label} tone={tag.tone} /> : null}
    </div>
  );
}

const Sub = ({ children }: { children: React.ReactNode }) => (
  <p className="text-body-small text-text-secondary">{children}</p>
);

/** "Your review": what changed, and the two answers. */
function Ask({
  review,
  version,
  onRequest,
  onApprove,
  busy,
}: {
  review: ClientReview;
  version: ClientVersion;
  onRequest: () => void;
  onApprove: () => void;
  busy: boolean;
}) {
  const { revisionCount, maxRevisions } = review.task;
  const spent = revisionCount >= maxRevisions;
  return (
    <section className={CARD}>
      <Head title="Your review" tag={{ label: 'IN REVIEW', tone: 'amber' }} />
      <Sub>
        Sent {sentAgo(version.sentAt)} by {madeBy(version)} · {revisionCount} of {maxRevisions} change rounds used
      </Sub>
      {version.whatChanged ? (
        <div className="flex flex-col gap-(--mbm-space-1) rounded-(--mbm-radius-md) bg-feedback-info-bg p-(--mbm-space-3-5)">
          <span className="text-label-medium text-feedback-info">What changed in v{version.versionNumber}</span>
          <p className="whitespace-pre-wrap text-body-small text-text-primary">{version.whatChanged}</p>
        </div>
      ) : null}
      <div className="flex gap-(--mbm-space-2-5)">
        <Button variant="secondary" size="review" className="flex-1" disabled={busy} onClick={onRequest}>
          Request changes
        </Button>
        <Button size="review" className="flex-1" disabled={busy} loading={busy} onClick={onApprove}>
          Approve
        </Button>
      </div>
      {spent ? (
        <p className="text-body-xsmall text-text-tertiary">
          You’ve used every change round on your plan. Request changes to see your options, or approve it as it is.
        </p>
      ) : null}
    </section>
  );
}

/**
 * "Need more changes?" (Figma C05.5a): every change round on the plan is spent, so asking for another is a choice
 * between approving as it is, buying one more round, or moving to a plan with unlimited ones.
 */
function NeedMore({
  review,
  version,
  busy,
  onClose,
  onApprove,
}: {
  review: ClientReview;
  version: ClientVersion;
  busy: boolean;
  onClose: () => void;
  onApprove: () => void;
}) {
  const toast = useToast();
  const type = TASK_TYPE_META[review.task.taskType];
  const { maxRevisions } = review.task;
  return (
    <section className={CARD}>
      <div className="flex items-center justify-between gap-(--mbm-space-3)">
        <h2 className="text-heading-h2 text-text-strong">Need more changes?</h2>
        <button
          type="button"
          aria-label="Close"
          onClick={onClose}
          className="flex size-(--mbm-space-7) items-center justify-center rounded-full bg-surface-field text-text-primary focus-visible:outline-none focus-visible:shadow-(--mbm-shadow-focus-ring)"
        >
          <Icon icon={X} size="sm" />
        </button>
      </div>
      <p className="flex items-center gap-(--mbm-space-2) pt-(--mbm-space-3) text-body-small text-text-secondary">
        <StatusTag label={type.short.toUpperCase()} tone="blue" />
        {review.task.title} · v{version.versionNumber}
      </p>
      <p className="text-label-medium text-text-primary">
        You’ve used {maxRevisions === 2 ? 'both' : 'all'} change rounds on your plan
      </p>
      <p className="rounded-(--mbm-radius-md) border-2 border-border-revealed bg-surface-card p-(--mbm-space-4) text-body-medium text-text-primary">
        Add one more round for {EXTRA_ROUND_PRICE}, or move to {plans.pro.name} for unlimited rounds. You can still
        approve v{version.versionNumber} as it is.
      </p>
      <p className="text-body-xsmall text-text-tertiary">
        {maxRevisions} of {maxRevisions} change rounds used
      </p>
      <div className="flex gap-(--mbm-space-2-5)">
        <Button variant="secondary" size="review" className="flex-1" disabled={busy} onClick={onApprove}>
          Approve instead
        </Button>
        <Button
          size="review"
          className="flex-1"
          onClick={() => toast(toasts.comingSoon('Buying an extra round arrives with billing.'))}
        >
          Add a round · {EXTRA_ROUND_PRICE}
        </Button>
      </div>
    </section>
  );
}

/** "Request changes": point at what needs changing, say it, and send it to the team (Figma C05.2 composer). */
function Composer({
  review,
  version,
  initial,
  editing,
  onClose,
  onDone,
  onApprove,
}: {
  review: ClientReview;
  version: ClientVersion;
  initial?: string;
  editing: boolean;
  onClose: () => void;
  onDone: () => void;
  onApprove: () => void;
}) {
  const toast = useToast();
  const respond = useClientRespond(review.task.id);
  const [areas, setAreas] = useState<string[]>([]);
  const [text, setText] = useState(initial ?? '');
  const [touched, setTouched] = useState(false);
  const short = text.trim().length < CLIENT_REVISION_MIN;
  const type = TASK_TYPE_META[review.task.taskType];
  const { revisionCount, maxRevisions } = review.task;

  const send = () => {
    setTouched(true);
    if (short) return;
    respond.mutate(
      { subId: version.id, input: { action: 'revise', feedback: text.trim(), areas } },
      {
        onSuccess: () => {
          toast({ title: editing ? 'Request updated' : 'Sent to your team', tone: 'success' });
          onDone();
        },
        onError: (err) => {
          const e = err as Partial<MappedApiError> & { code?: string };
          toast({
            title: e.code === REVISIONS_USED_CODE || e.status === 403 ? 'No change rounds left' : 'Couldn’t send that',
            description:
              e.code === REVISIONS_USED_CODE || e.status === 403
                ? 'You’ve used every change round on your plan.'
                : 'Please try again.',
            tone: 'error',
          });
        },
      },
    );
  };

  return (
    <section className={CARD}>
      <div className="flex items-center justify-between gap-(--mbm-space-3)">
        <h2 className="text-heading-h2 text-text-strong">Request changes</h2>
        <button
          type="button"
          aria-label="Close"
          onClick={onClose}
          className="flex size-(--mbm-space-7) items-center justify-center rounded-full bg-surface-field text-text-primary focus-visible:outline-none focus-visible:shadow-(--mbm-shadow-focus-ring)"
        >
          <Icon icon={X} size="sm" />
        </button>
      </div>
      <p className="flex items-center gap-(--mbm-space-2) pt-(--mbm-space-3) text-body-small text-text-secondary">
        <StatusTag label={type.short.toUpperCase()} tone="blue" />
        {review.task.title} · v{version.versionNumber}
      </p>
      <fieldset className="flex flex-col gap-(--mbm-space-2-5)">
        <legend className="mb-(--mbm-space-2-5) text-label-medium text-text-primary">What needs changing?</legend>
        <div className="flex flex-wrap gap-(--mbm-space-2)">
          {feedbackAreas(review.task.taskType, version.assets.length).map((area) => {
            const on = areas.includes(area);
            return (
              <button
                key={area}
                type="button"
                aria-pressed={on}
                onClick={() => setAreas((all) => (on ? all.filter((a) => a !== area) : [...all, area]))}
                className={cx(
                  'h-(--mbm-size-toolbar-control) rounded-full border px-(--mbm-space-3-5) text-label-medium focus-visible:outline-none focus-visible:shadow-(--mbm-shadow-focus-ring)',
                  on
                    ? 'border-feedback-info bg-feedback-info-bg text-feedback-info'
                    : 'border-border-default bg-surface-card text-text-primary',
                )}
              >
                {area}
              </button>
            );
          })}
        </div>
      </fieldset>
      <div className="flex flex-col gap-(--mbm-space-1-5)">
        <textarea
          aria-label="What should change"
          value={text}
          rows={4}
          onChange={(e) => setText(e.target.value)}
          placeholder="Say what you’d like different"
          className={cx(
            'w-full resize-none rounded-(--mbm-radius-md) border bg-surface-card p-(--mbm-space-4) text-body-medium text-text-primary outline-none placeholder:text-text-muted',
            'focus:outline-2 focus:-outline-offset-2 focus:outline-focus-ring',
            touched && short ? 'border-feedback-error' : 'border-border-default',
          )}
        />
        {touched && short ? (
          <p role="alert" className="text-body-xsmall text-feedback-error">
            Tell the team what to change (at least {CLIENT_REVISION_MIN} characters)
          </p>
        ) : null}
      </div>
      <div className="flex items-center justify-between gap-(--mbm-space-3)">
        <button
          type="button"
          onClick={() => toast(toasts.comingSoon('Attaching a reference arrives with the Work step.'))}
          className="inline-flex h-(--mbm-size-button-row) items-center gap-(--mbm-space-2) rounded-full bg-surface-field px-(--mbm-space-3-5) text-label-medium text-text-primary focus-visible:outline-none focus-visible:shadow-(--mbm-shadow-focus-ring)"
        >
          <Icon icon={UploadSimple} size="sm" />
          Add a reference
        </button>
        <span className="text-body-xsmall text-text-tertiary">
          {editing
            ? 'No extra round used'
            : `Uses round ${Math.min(revisionCount + 1, maxRevisions)} of ${maxRevisions}`}
        </span>
      </div>
      <div className="flex gap-(--mbm-space-2-5)">
        {editing ? null : (
          <Button variant="secondary" size="review" className="flex-1" disabled={respond.isPending} onClick={onApprove}>
            Approve instead
          </Button>
        )}
        <Button size="review" className="flex-1" loading={respond.isPending} onClick={send}>
          {editing ? 'Save request' : 'Send to team'}
        </Button>
      </div>
    </section>
  );
}

/** After they asked: what they asked, and that the team is on it. */
function RevisionRequested({
  review,
  version,
  onEdit,
}: {
  review: ClientReview;
  version: ClientVersion;
  onEdit: () => void;
}) {
  const toast = useToast();
  const who = version.sentBy?.name ?? 'Your team';
  const by = review.task.deadline ? ` · new version by ${weekdayDay(review.task.deadline)}` : '';
  return (
    <section className={CARD}>
      <div className="flex flex-col gap-(--mbm-space-0-5)">
        <h2 className="text-heading-h3 text-text-primary">Revision requested</h2>
        <p className="text-body-xsmall text-text-secondary">
          {who} is on it{by}
        </p>
      </div>
      <p className="rounded-(--mbm-radius-md) bg-surface-tint p-(--mbm-space-3-5) text-body-small text-text-secondary">
        You asked: <span className="font-semibold text-text-primary">“{wordsOf(version.feedback)}”</span>
      </p>
      <div className="flex gap-(--mbm-space-2-5)">
        <Button variant="secondary" size="review" icon={PencilSimple} className="flex-auto" onClick={onEdit}>
          Edit request
        </Button>
        <Button
          size="review"
          icon={ChatCircle}
          className="flex-auto"
          onClick={() => toast(toasts.comingSoon('Chat arrives with the messaging step.'))}
        >
          Message {who}
        </Button>
      </div>
    </section>
  );
}

/** Approved: it is done. Downloading works now; posting from here needs the Instagram connection. */
function Approved({ review, version }: { review: ClientReview; version: ClientVersion }) {
  const toast = useToast();
  const d = version.details;
  const planned =
    (d.type === 'post' || d.type === 'reel') && d.scheduledUploadAt ? goesLive(d.scheduledUploadAt) : null;
  const download = async () => {
    try {
      for (const asset of version.assets)
        window.open(await signedDownloadUrl(asset.key), '_blank', 'noopener,noreferrer');
    } catch {
      toast({ title: 'Couldn’t open the files', tone: 'error' });
    }
  };
  return (
    <section className={CARD}>
      <div className="flex items-center gap-(--mbm-space-2-5)">
        <h2 className="text-heading-h3 text-text-primary">Approved</h2>
        <StatusTag label="APPROVED" tone="green" />
      </div>
      <p className="text-body-xsmall text-text-secondary">
        {planned ? `Planned for ${planned}` : 'Thanks, your team has been told.'}
        {review.task.taskType === 'post' ? ' · or post it now' : ''}
      </p>
      <div className="flex gap-(--mbm-space-2-5)">
        <Button
          variant="secondary"
          size="review"
          icon={DownloadSimple}
          className="flex-auto"
          disabled={version.assets.length === 0}
          onClick={() => void download()}
        >
          Download
        </Button>
        {review.task.taskType === 'post' || review.task.taskType === 'reel' ? (
          <Button
            size="review"
            icon={InstagramLogo}
            className="flex-auto"
            onClick={() => toast(toasts.comingSoon('Posting from here needs your Instagram connected.'))}
          >
            Post on Instagram
          </Button>
        ) : null}
      </div>
    </section>
  );
}

/** An older version: it was replaced, and what the client said about it is kept. */
function Replaced({ version, current }: { version: ClientVersion; current: ClientVersion }) {
  return (
    <section className={CARD}>
      <Head title={`Version ${version.versionNumber}`} />
      <Sub>
        Replaced by v{current.versionNumber}, sent {sentAgo(current.sentAt)}.
      </Sub>
      {version.feedback ? (
        <p className="rounded-(--mbm-radius-md) bg-surface-tint p-(--mbm-space-3-5) text-body-small text-text-secondary">
          You asked: <span className="font-semibold text-text-primary">“{wordsOf(version.feedback)}”</span>
        </p>
      ) : null}
    </section>
  );
}

/** Published with no approval needed: nothing to answer. */
function Published({ version }: { version: ClientVersion }) {
  return (
    <section className={CARD}>
      <Head title="Done" tag={{ label: 'PUBLISHED', tone: 'green' }} />
      <Sub>
        Sent {sentAgo(version.sentAt)} by {madeBy(version)}. Nothing for you to approve.
      </Sub>
    </section>
  );
}

export function DetailsCard({ review, version }: { review: ClientReview; version: ClientVersion }) {
  return (
    <section className={CARD}>
      <h2 className="text-heading-h3 text-text-primary">Details</h2>
      <dl className="flex flex-col gap-(--mbm-space-3)">
        {detailRows(review, version).map((row) => (
          <div key={row.label} className="flex items-baseline justify-between gap-(--mbm-space-4)">
            <dt className="text-body-small text-text-tertiary">{row.label}</dt>
            <dd className="text-right text-label-medium text-text-strong">{row.value}</dd>
          </div>
        ))}
      </dl>
    </section>
  );
}

export function CaptionCard({ version }: { version: ClientVersion }) {
  const toast = useToast();
  const caption = captionOf(version);
  if (!caption) return null;
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(caption);
      toast({ title: 'Copied', tone: 'success' });
    } catch {
      toast({ title: 'Couldn’t copy', description: 'Select the text and copy it.', tone: 'error' });
    }
  };
  return (
    <section className={CARD}>
      <div className="flex items-center justify-between gap-(--mbm-space-3)">
        <h2 className="text-heading-h3 text-text-primary">Caption</h2>
        <button
          type="button"
          onClick={() => void copy()}
          className="inline-flex items-center gap-(--mbm-space-1-5) text-label-medium text-brand-primary focus-visible:outline-none focus-visible:shadow-(--mbm-shadow-focus-ring)"
        >
          <Icon icon={Copy} size="sm" />
          Copy
        </button>
      </div>
      <p className="whitespace-pre-wrap text-body-small text-text-secondary">{caption}</p>
    </section>
  );
}

/**
 * The panel beside the stage (Figma C05.2): whichever of "Your review", the request composer, "Revision requested" or
 * "Approved" fits where the chosen version stands, then its details and caption.
 */
export function ReviewPanel({ review, version }: { review: ClientReview; version: ClientVersion }) {
  const toast = useToast();
  const respond = useClientRespond(review.task.id);
  const [composing, setComposing] = useState(false);
  const current = review.versions[0]!;
  const isCurrent = version.id === current.id;

  const approve = () =>
    respond.mutate(
      { subId: version.id, input: { action: 'approve' } },
      {
        onSuccess: () => {
          setComposing(false);
          toast({ title: 'Approved. Nice one!', description: 'Your team has been told.', tone: 'success' });
        },
        onError: () => toast({ title: 'Couldn’t approve that', description: 'Please try again.', tone: 'error' }),
      },
    );

  const answerable = review.answerableId === version.id;
  const asked = isCurrent && version.clientStatus === 'revision_requested';
  const approved = isCurrent && version.clientStatus === 'approved';

  const spent = review.task.revisionCount >= review.task.maxRevisions;

  const main = !isCurrent ? (
    <Replaced version={version} current={current} />
  ) : composing && answerable && spent ? (
    <NeedMore
      review={review}
      version={version}
      busy={respond.isPending}
      onClose={() => setComposing(false)}
      onApprove={approve}
    />
  ) : composing && (answerable || asked) ? (
    <Composer
      review={review}
      version={version}
      initial={asked ? wordsOf(version.feedback) : undefined}
      editing={asked}
      onClose={() => setComposing(false)}
      onDone={() => setComposing(false)}
      onApprove={approve}
    />
  ) : answerable ? (
    <Ask
      review={review}
      version={version}
      busy={respond.isPending}
      onRequest={() => setComposing(true)}
      onApprove={approve}
    />
  ) : asked ? (
    <RevisionRequested review={review} version={version} onEdit={() => setComposing(true)} />
  ) : approved ? (
    <Approved review={review} version={version} />
  ) : (
    <Published version={version} />
  );

  return (
    <div className="flex flex-col gap-(--mbm-space-4)">
      {main}
      <DetailsCard review={review} version={version} />
      <CaptionCard version={version} />
    </div>
  );
}
